import * as THREE from 'three';
import { CharacterController } from '../physics/CharacterController.js';
import { EV } from '../core/EventBus.js';

const _fwd = new THREE.Vector3();
const _eye = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _tmp = new THREE.Vector3();

const HALF_PI = Math.PI / 2 - 0.001;

/**
 * The camera rig. Movement lives in CharacterController; this adds the parts
 * that only matter because there is a human behind the viewport: where the
 * head is, how much the field of view widens with speed, and how a portal
 * transit rewrites which way "forward" is.
 */
export class Player {
  constructor({ engine, world, portals, bus, settings, audio }) {
    this.engine = engine;
    this.camera = engine.camera;
    this.bus = bus;
    this.settings = settings;
    this.audio = audio;
    this.portals = portals;

    this.controller = new CharacterController(world, {
      // Mid-air steer authority. Playtested at 4.5 ("can't even reach a
      // close platform") and 9 ("squirrelly, fling yourself all over"):
      // 6.5 is the tuned midpoint.
      // Traced 2026-08-14 after "can't glide to the platform" reports: full
      // input reaches this cap in ~50ms and covers 10m of drift in 1.6s, so
      // the authority is real; the reports came from not knowing air steer
      // exists (tutorial) and from half-deflected touch sticks (response
      // curve). Raising this instead re-tunes every walker-verified sector.
      airWishCap: 6.5,
    });
    this.yaw = 0;
    this.pitch = 0;
    this.roll = 0;

    this.baseFov = settings.get('fov');
    this._fovKick = 0;
    this._bob = 0;
    this._bobAmount = 0;
    this._landDip = 0;
    this._alive = true;

    this.checkpoint = { position: new THREE.Vector3(), yaw: 0 };

    this.controller.events.onLand = (impact) => {
      this._landDip = Math.min(0.32, impact * 0.006);
      this.audio?.land(impact);
      this.bus.emit(EV.PLAYER_LANDED, { impact });
    };
    this.controller.events.onJump = () => {
      this.audio?.jump();
      this.bus.emit(EV.PLAYER_JUMPED, {});
    };
    this.controller.events.onStep = () => this.audio?.step();

    // Traversal runs inside the movement substep so a fall converts into a
    // launch instead of being absorbed by the tunnel floor first.
    this.controller.onSubstep = (subDt) => portals.updateTraversal(subDt, 'player');

    portals.registerBody('player', {
      getSamplePoints: (out) => {
        out[0].copy(this.controller.capsule.start);
        out[1].copy(this.controller.capsule.end);
        return out;
      },
      onTeleport: (matrix, from, to) => this.onTeleport(matrix, from, to),
    });
  }

  setCheckpoint(position, yaw = null) {
    this.checkpoint.position.copy(position);
    if (yaw != null) this.checkpoint.yaw = yaw;
  }

  spawn(position, yaw = 0) {
    this.controller.teleportTo(position.x, position.y, position.z);
    this.yaw = yaw;
    this.pitch = 0;
    this.roll = 0;
    this._fovKick = 0;
    this._landDip = 0;
    this._alive = true;
    this.setCheckpoint(position, yaw);
    this.syncCamera(0);
    this.bus.emit(EV.PLAYER_SPAWN, { position });
  }

  respawn() {
    const p = this.checkpoint.position;
    this.controller.teleportTo(p.x, p.y, p.z);
    this.yaw = this.checkpoint.yaw;
    this.pitch = 0;
    this._alive = true;
    this.syncCamera(0);
  }

  get speed() { return this.controller.speed; }
  get position() { return this.controller.position; }

  forward(out = _fwd) {
    const cp = Math.cos(this.pitch);
    return out.set(-Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp);
  }

  eye(out = _eye) { return this.controller.getEyePosition(out); }

  look(dx, dy) {
    this.yaw -= dx;
    this.pitch -= dy;
    this.pitch = Math.max(-HALF_PI, Math.min(HALF_PI, this.pitch));
  }

  update(dt, input) {
    this.controller.update(dt, {
      moveX: input.moveX,
      moveZ: input.moveZ,
      yaw: this.yaw,
      jump: input.jump,
      jumpPressed: input.jumpPressed,
      sprint: input.sprint,
    });
    this.syncCamera(dt, input);
  }

  syncCamera(dt, input = null) {
    const c = this.controller;
    const speed = c.horizontalSpeed;

    // Head bob only while actually walking on something.
    if (dt > 0) {
      const walking = c.grounded && speed > 1.2;
      this._bobAmount += ((walking ? Math.min(1, speed / c.walkSpeed) : 0) - this._bobAmount) * Math.min(1, dt * 8);
      this._bob += dt * speed * 1.15;
      this._landDip = Math.max(0, this._landDip - dt * 1.5);

      // Field of view opens up with speed. This is the single strongest cue
      // that a fling worked, so it leads the actual velocity slightly.
      const target = Math.min(20, Math.max(0, (c.speed - 9) * 0.42));
      this._fovKick += (target - this._fovKick) * Math.min(1, dt * (target > this._fovKick ? 6 : 2.4));
      const fov = this.engine.verticalFovFor(this.baseFov) + this._fovKick;
      if (Math.abs(this.camera.fov - fov) > 0.01) {
        this.camera.fov = fov;
        this.camera.updateProjectionMatrix();
      }

      const wantRoll = (input ? -input.moveX : 0) * 0.028 + (c.grounded ? 0 : -c.velocity.x * 0.0004);
      this.roll += (wantRoll - this.roll) * Math.min(1, dt * 6);
    }

    this.eye(_tmp);
    const bobY = Math.sin(this._bob * 2) * 0.045 * this._bobAmount;
    const bobX = Math.cos(this._bob) * 0.035 * this._bobAmount;
    this.camera.position.set(
      _tmp.x + bobX * Math.cos(this.yaw),
      _tmp.y + bobY - this._landDip,
      _tmp.z - bobX * Math.sin(this.yaw),
    );
    this.camera.rotation.set(this.pitch, this.yaw, this.roll + bobX * 0.12, 'YXZ');
    this.camera.updateMatrixWorld(true);
  }

  /**
   * Rewrite position, velocity and view direction into the destination
   * portal's frame. Roll is deliberately dropped: coming out of a floor portal
   * should leave you upright, not sideways.
   */
  onTeleport(matrix, from, to) {
    const speedBefore = this.controller.speed;
    this.controller.applyTransform(matrix);

    _q.setFromRotationMatrix(matrix);
    this.forward(_fwd).applyQuaternion(_q).normalize();
    this.yaw = Math.atan2(-_fwd.x, -_fwd.z);
    this.pitch = Math.max(-HALF_PI, Math.min(HALF_PI, Math.asin(THREE.MathUtils.clamp(_fwd.y, -1, 1))));

    this.audio?.teleport(speedBefore);
    this.syncCamera(0);
  }

  /** Sample points used by level trigger volumes. */
  triggerPoints(out) {
    const c = this.controller;
    out[0].copy(c.position);
    out[1].set(c.position.x, c.position.y + c.height * 0.5, c.position.z);
    out[2].set(c.position.x, c.position.y + c.height, c.position.z);
    return out;
  }

  onFovSetting(fov) {
    this.baseFov = fov;
  }
}
